import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import { WidgetDataResult, WidgetChartConfig, CardGridMetricDef } from '../types';
import { useCurrentUser } from '../../../contexts/CurrentUser';
import { useDashboard } from '../contexts/DashboardContext';
import { ampli } from '../../../ampli';
import { ReactComponent as PositiveSentimentIcon } from '../assets/positiveSentiment.svg';
import { ReactComponent as NeutralSentimentIcon } from '../assets/neutralSentiment.svg';
import { ReactComponent as NegativeSentimentIcon } from '../assets/negativeSentiment.svg';
import { ReactComponent as InfoIcon } from '../../../pages/SourcePage/assets/anchor.svg';
import { normalizeSentiment } from './sentimentColors';
import styles from './ChartOpinions.module.scss';

const DEFAULT_CARDS_PER_PAGE = 9;

function formatCompact(n: number): string {
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1).replace(/\.0$/, '')}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
  return n.toLocaleString();
}

function formatMetricValue(raw: unknown, format?: CardGridMetricDef['format']): string {
  if (raw === null || raw === undefined) return '—';
  const num = Number(raw);
  if (!isNaN(num) && format !== 'number') return formatCompact(num);
  return String(raw);
}

function SentimentIcon({ sentiment }: { sentiment: string }) {
  switch (sentiment.trim().toLowerCase()) {
    case 'positive':
      return <PositiveSentimentIcon aria-hidden="true" />;
    case 'negative':
      return <NegativeSentimentIcon aria-hidden="true" />;
    default:
      return <NeutralSentimentIcon aria-hidden="true" />;
  }
}

interface ChartOpinionsProps {
  data: WidgetDataResult;
  config: WidgetChartConfig;
}

export const ChartOpinions: React.FC<ChartOpinionsProps> = ({ data, config }) => {
  const [page, setPage] = useState(0);
  const [currentUser] = useCurrentUser();
  const { displayedDashboard } = useDashboard();
  const cfg = config.opinionsConfig ?? {};
  const metrics = cfg.metrics ?? [];
  const showMetrics = cfg.showMetrics !== false;
  const clickUrlTemplate = cfg.clickUrlTemplate;
  const clickTarget = cfg.clickTarget ?? '_blank';
  const hasMetricTooltips = metrics.some((m) => m.tooltip);
  const cardsPerPage = Number.isInteger(cfg.cardsPerPage) && cfg.cardsPerPage! > 0
    ? cfg.cardsPerPage!
    : DEFAULT_CARDS_PER_PAGE;
  const sentimentBackground = cfg.sentimentBackground === true;

  useEffect(() => { setPage(0); }, [data.rows, cardsPerPage]);

  const { totalPages, pageRows } = useMemo(() => {
    const total = Math.ceil(data.rows.length / cardsPerPage);
    const rows = data.rows.slice(page * cardsPerPage, (page + 1) * cardsPerPage);
    return { totalPages: total, pageRows: rows };
  }, [data.rows, page, cardsPerPage]);

  // Auto-detect columns if not explicitly configured
  const titleCol = cfg.titleCol
    ?? data.columns.find((c) => /title|name|label|heading|subject/i.test(c))
    ?? data.columns.find((c) => {
      const v = data.rows[0]?.[c];
      return typeof v === 'string' && v.length > 20;
    })
    ?? data.columns[0];
  const sentimentCol = cfg.sentimentCol ?? data.columns.find((c) => /sentiment/i.test(c));

  const buildUrl = (row: Record<string, unknown>): string | null => {
    if (!clickUrlTemplate) return null;
    const isExternalMode = /^\{\w+\}$/.test(clickUrlTemplate.trim());
    return clickUrlTemplate.replace(/\{(\w+)\}/g, (_, col) => {
      if (row[col] == null) return '';
      const val = String(row[col]);
      return isExternalMode && /^https?:\/\//i.test(val) ? val : encodeURIComponent(val);
    });
  };

  const handleCardClick = useCallback((cardUrl: string) => {
    ampli.track({
      event_type: 'Widget click action',
      event_properties: {
        user_id: currentUser?.id,
        workspace_id: currentUser?.workspace_id,
        dashboard_id: displayedDashboard?.id,
        widget_type: 'opinions',
        action_type: 'url',
        is_external: /^https?:\/\//i.test(cardUrl),
      },
    });
    window.open(cardUrl, clickTarget);
  }, [currentUser, displayedDashboard?.id, clickTarget]);

  const handlePrev = () => setPage((p) => Math.max(0, p - 1));
  const handleNext = () => setPage((p) => Math.min(totalPages - 1, p + 1));

  return (
    <div className={styles.root}>
      <div className={styles.grid} role="list">
        {pageRows.map((row, i) => {
          const title = titleCol ? String(row[titleCol] ?? '') : '';
          const sentiment = sentimentCol ? String(row[sentimentCol] ?? '') : '';
          const normalizedSentiment = sentimentBackground ? normalizeSentiment(sentiment) : null;
          const cardUrl = buildUrl(row);

          return (
            <article
              key={page * cardsPerPage + i}
              className={`${styles.card} ${cardUrl ? styles.cardClickable : ''}`}
              role={cardUrl ? 'link' : 'listitem'}
              onClick={cardUrl ? () => handleCardClick(cardUrl) : undefined}
              onKeyDown={cardUrl ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCardClick(cardUrl); } } : undefined}
              tabIndex={cardUrl ? 0 : undefined}
              aria-label={cardUrl && title ? `Open ${title}` : undefined}
              data-sentiment={normalizedSentiment ?? undefined}
            >
              <span
                className={styles.sentimentIcon}
                role="img"
                aria-label={sentiment ? `Sentiment: ${sentiment.toLowerCase()}` : 'Sentiment'}
              >
                <SentimentIcon sentiment={sentiment} />
              </span>
              <div className={styles.content}>
                {title && <h3 className={styles.title}>{title}</h3>}
                {showMetrics && metrics.length > 0 && (
                  <dl className={styles.metrics}>
                    {metrics.map((m, index) => {
                      const tooltipId = `opinions-info-tooltip_${page}_${i}_${m.col}_${index}`;
                      return (
                        <div key={m.col} className={styles.metricItem}>
                          <dt className={styles.metricLabel}>{m.label}:</dt>
                          <dd className={styles.metricValue}>{formatMetricValue(row[m.col], m.format)}</dd>
                          {m.tooltip && (
                            <button
                              type="button"
                              className={styles.infoBtn}
                              aria-label={m.tooltip}
                              data-tooltip-id={tooltipId}
                              data-tooltip-content={m.tooltip}
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => e.stopPropagation()}
                            >
                              <InfoIcon className={styles.infoIcon} aria-hidden="true" />
                            </button>
                          )}
                          <Tooltip id={tooltipId} place="bottom" style={{ 'zIndex': '15', maxWidth: '250px' }} />
                        </div>
                      );
                    })}
                  </dl>
                )}
              </div>
            </article>
          );
        })}
      </div>

      {/* {hasMetricTooltips && <Tooltip id="opinions-info-tooltip" place="bottom" isOpen={true} style={{ 'zIndex': '15', maxWidth: '250px' }} />} */}

      {totalPages > 1 && (
        <nav className={styles.pagination} aria-label="Opinions pagination">
          <button
            type="button"
            className={styles.pageBtn}
            onClick={handlePrev}
            disabled={page === 0}
            aria-label="Previous page"
          >
            ‹
          </button>
          <span className={styles.pageNum} aria-live="polite" aria-atomic="true">
            {page + 1} / {totalPages}
          </span>
          <button
            type="button"
            className={styles.pageBtn}
            onClick={handleNext}
            disabled={page === totalPages - 1}
            aria-label="Next page"
          >
            ›
          </button>
        </nav>
      )}
    </div>
  );
};
